import { json, redirect, type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { fetchJobPost } from "~/utils/fetch_job_post";
import { JobPost } from "~/components/job_posts/job_post";
import { ApplicationForm } from "~/components/job_posts/application_form";
import { Logo } from "~/components/logo";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { buildJobPostTags } from "~/utils/job_helpers";
import { useRef } from "react";
import { focusForm } from "~/utils/focus_form";
import { isClient } from "~/utils/env_check";
import { loadGooglePicker } from "~/utils/load_google_picker";
import useRecruitics from "~/hooks/use_recruitics";
import { withNamespace } from "~/utils/translation";
import config from "~/config.server";
import { useTrackingParams } from "~/hooks/use_tracking_params";
import { QuickApply } from "~/quick_apply/quick_apply";
import { ApplyWithSeek } from "~/apply_with_seek/apply_with_seek";
import { createPostOpenGraphTags } from "~/utils/build_page_metadata";
import useAppcast, { AppcastEvent } from "~/hooks/use_appcast";
import { useRedirectForEmbeddedBoards } from "~/hooks/use_redirect_for_embedded_boards";
import { EmbedToken } from "~/utils/embed/embed_token.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const currentUrl = new URL(request.url);

  const urlToken = currentUrl.searchParams.get("for") || "";
  const jobPostId = currentUrl.searchParams.get("token") || "";
  const internal = currentUrl.searchParams.get("internal") === "true" ? true : false;

  const validityToken = currentUrl.searchParams.get("validityToken");

  EmbedToken.from(validityToken).validateFor(urlToken, jobPostId);

  // Temporarily throw 404 until the embedded internal boards is activated
  if (internal && config.get("activate_embedded_internal_boards") !== "true") {
    throw new Response(null, { status: 404 });
  }

  const { jobPost, status: jobApplicationStatus } = await fetchJobPost({
    internal,
    urlToken,
    jobPostId,
    apiVersion: "v2",
    includeQuestions: true,
    includeEducation: true,
  });

  if (jobApplicationStatus === 404 || !jobPost || !jobPost.questions) {
    let path = `/embed/job_board?for=${urlToken}&error=true`;

    if (internal) {
      path += "&internal=true";
    }
    return redirect(path);
  }

  let confirmationPath = `/embed/job_app/confirmation?for=${urlToken}&token=${jobPostId}`;
  const submitPath = `${config.get("domains.jben_url")}/embed/${urlToken}/jobs/${jobPostId}`;

  if (internal) {
    confirmationPath += "&internal=true";
  }

  return json({
    internal,
    jobPost,
    urlToken,
    submitPath,
    confirmationPath,
    quickApply: {
      active: config.get("quickApply.active") as boolean,
      magicActive: config.get("quickApply.magicActive") as boolean,
      url: config.get("job_seekers.url") as string,
      metadata: {
        jobPostId: +jobPostId,
      },
    },
    seek: {
      applyWithSeekEnabled: config.get("seek.applyWithSeekEnabled") as boolean,
    },
  });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const t = withNamespace("job_post");

  if (!data) {
    return [];
  }

  const { title, company_name: companyName } = data.jobPost;
  return [
    {
      title: t("page_title", {
        title,
        companyName,
        interpolation: { escapeValue: false },
      }),
      ...createPostOpenGraphTags(data.jobPost),
    },
  ];
};

export const handle = { i18n: "job_post" };

let loadedGooglePicker: boolean;

export default function EmbeddableJobPost() {
  const { internal, jobPost, urlToken, submitPath, confirmationPath, quickApply, seek } =
    useLoaderData<typeof loader>();
  const {
    allow_external_notifications: allowExternalNotifications,
    enable_recruitics: enableRecruitics,
    appcast_tracking_code: appcastTrackingCode,
    embed_internal_board: embedInternalBoard,
    application_tracking_enabled: applicationTrackingEnabled,
    logo,
    google_drive_enabled,
    display_new_jobs_tag: displayNewJobsTag,
    seek_id,
  } = useBoardConfiguration();

  const { withTrackingParams } = useTrackingParams();

  const formRef = useRef<HTMLFormElement>(null);

  const shouldLoadGooglePicker = isClient() && google_drive_enabled;
  const quickApplyActive =
    quickApply.active && (allowExternalNotifications || applicationTrackingEnabled) && !internal;

  const applyWithSeekActive = Boolean(
    seek?.applyWithSeekEnabled && seek_id && jobPost?.apply_with_seek
  );

  if (!loadedGooglePicker && shouldLoadGooglePicker && !internal) {
    loadGooglePicker();
    loadedGooglePicker = true;
  }

  useRedirectForEmbeddedBoards(internal, embedInternalBoard);
  useRecruitics(enableRecruitics, internal);
  useAppcast(AppcastEvent.JobView, appcastTrackingCode, internal);

  return (
    <QuickApply
      active={quickApplyActive}
      magicActive={quickApply.magicActive}
      url={quickApply.url}
      metadata={quickApply.metadata}
    >
      <ApplyWithSeek active={applyWithSeekActive}>
        <div className="job__embedded-wrapper">
          <Logo
            companyName={jobPost.company_name}
            url={logo.url}
            href={logo.href && withTrackingParams(logo.href)}
          />
          <JobPost
            content={jobPost.content}
            conclusion={jobPost.conclusion}
            introduction={jobPost.introduction}
            location={jobPost.job_post_location}
            title={jobPost.title}
            internal={internal}
            payRanges={jobPost.pay_ranges}
            tags={buildJobPostTags(jobPost, displayNewJobsTag, true)}
            onApplyClick={() => focusForm(formRef.current)}
            hiringPlanId={jobPost.hiring_plan_id}
            hiringTeamMembers={jobPost.hiring_team_members}
            urlToken={urlToken}
            companyName={jobPost.company_name}
            allowJobAlert={allowExternalNotifications}
          />
          <div className="divider" role="separator"></div>
          <ApplicationForm
            jobPost={jobPost}
            demographicSection={jobPost.demographic_questions}
            questions={jobPost.questions}
            submitPath={submitPath}
            confirmationPath={withTrackingParams(confirmationPath)}
            embedded={true}
            internal={internal}
            formRef={formRef}
          />
        </div>
      </ApplyWithSeek>
    </QuickApply>
  );
}
